"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetAdDetails = exports.googleAdsGetAdDetailsResultSchema = exports.googleAdsGetAdDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetAdDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically use the selected account. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific campaign ID to analyze. If not provided, returns data for all campaigns in the account.'),
    adGroupId: zod_1.z
        .string()
        .nullish()
        .describe('Specific ad group ID to analyze. If not provided, returns data for all ad groups.'),
    adId: zod_1.z
        .string()
        .nullish()
        .describe('Specific ad ID to analyze. If not provided, returns data for all ads.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. If omitted, returns lifetime data from ad creation.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. If omitted, returns data up to today.'),
    metrics: zod_1.z
        .array(zod_1.z.enum(['impressions', 'clicks', 'cost', 'conversions', 'ctr', 'cpc', 'cpm']))
        .nullish()
        .default(['impressions', 'clicks', 'cost', 'ctr'])
        .describe('Metrics to include in the response. Defaults to basic metrics.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of ads to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    includeDisabled: zod_1.z
        .boolean()
        .default(false)
        .describe('Include paused, removed, or disabled ads in results. Set to true to show all ads regardless of status. Defaults to false.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no ad data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
    })
        .describe('Performance query options'),
})
    .describe('Get Google Ads ad performance metrics for all campaign types.');
exports.googleAdsGetAdDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetAdDetailsInputSchema);
var googleAdsGetAdDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    adId: zod_1.z.string(),
    adName: zod_1.z.string().nullish(),
    adType: zod_1.z.string().nullish(),
    adGroupId: zod_1.z.string(),
    adGroupName: zod_1.z.string().nullish(),
    campaignId: zod_1.z.string(),
    campaignName: zod_1.z.string().nullish(),
    status: zod_1.z.enum(['UNKNOWN', 'UNSPECIFIED', 'ENABLED', 'PAUSED', 'REMOVED']).nullish(),
    adStrength: zod_1.z.string().nullish(),
    performance: zod_1.z.object({
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
        conversions: zod_1.z.number().nullish(),
        ctr: zod_1.z.number().nullish(),
        cpc: zod_1.z.number().nullish(),
        cpm: zod_1.z.number().nullish(),
        conversionRate: zod_1.z.number().nullish(),
    }),
    dailyBreakdown: zod_1.z
        .array(zod_1.z.object({
        date: zod_1.z.string(),
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
    }))
        .nullish(),
}));
exports.googleAdsGetAdDetailsResultSchema = googleAdsGetAdDetailsResponseSchema;
exports.googleAdsGetAdDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetAdDetails',
    displayName: 'Google Ads Ad Performance',
    displayDescription: 'Get performance metrics and analytics for Google Ads ads across all campaign types. Automatically uses the selected account unless user provides specific customer ID.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetAdDetailsArgsSchema,
}, googleAdsGetAdDetailsResponseSchema);
